/**
 * WVB-01 — Event Graph Explorer (flat list, no recursive JSX)
 * Uses a flat array with depth-based indentation to avoid Babel plugin stack overflow.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, ExternalLink } from "lucide-react";
import { NODE_CONFIG, navTarget, STATUS_STYLES } from "./EventGraphUtils";

function StatusPill({ status }) {
  if (!status) return null;
  const cls = STATUS_STYLES[status.toUpperCase()] || "bg-slate-50 text-slate-500";
  return (
    <span className={`inline-block px-1.5 py-0 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function NodeRow({ node, depth, isOpen, hasChildren, onToggle, navigate }) {
  const cfg = NODE_CONFIG[node.node_type] || NODE_CONFIG.DOCUMENT;
  const url = navTarget(node.node_type, node.id);
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors group"
      style={{ paddingLeft: `${8 + depth * 18}px` }}
      onClick={onToggle}
      data-testid={`explorer-node-${node.id}`}
    >
      <span className="w-4 flex-shrink-0 text-slate-400">
        {hasChildren
          ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)
          : <span className="inline-block w-3.5" />}
      </span>
      <span
        className="text-xs font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
        style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
      >
        {cfg.label}
      </span>
      <span className="text-sm text-slate-800 font-medium truncate flex-1">{node.label}</span>
      {node.status && <StatusPill status={node.status} />}
      {node.subtitle && (
        <span className="text-xs text-slate-400 truncate max-w-24">{node.subtitle}</span>
      )}
      {url && (
        <button
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-600 transition-all"
          onClick={e => { e.stopPropagation(); navigate(url); }}
          data-testid={`explorer-nav-${node.id}`}
          title={`Open ${cfg.label}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function buildFlatList(graphData) {
  const byType = {};
  for (const n of graphData.nodes) {
    if (!byType[n.node_type]) byType[n.node_type] = [];
    byType[n.node_type].push(n);
  }

  const upstreamTypes = ["ACCOUNT", "LEAD", "OPPORTUNITY", "BOOKING"];
  const upstream = upstreamTypes.flatMap(t => (byType[t] || []).map(n => ({ node: n, depth: 0, parentKey: null, key: n.id })));

  const sections = [
    { types: ["SCOPE_LINE"],          label: "Scope Lines" },
    { types: ["PARTNER_ASSIGNMENT"],  label: "Partners" },
    { types: ["EVENT_FUNCTION"],      label: "Functions" },
    { types: ["EXECUTION_STREAM"],    label: "Execution Streams" },
    { types: ["STREAM_MEMBER"],       label: "Stream Members" },
    { types: ["MILESTONE"],           label: "Milestones" },
    { types: ["TASK"],                label: "Tasks" },
    { types: ["READINESS_ITEM"],      label: "Readiness" },
    { types: ["ISSUE"],               label: "Issues" },
    { types: ["DOCUMENT"],            label: "Documents" },
  ];

  const downstream = [];
  for (const s of sections) {
    const items = s.types.flatMap(t => byType[t] || []);
    if (!items.length) continue;
    const sectionKey = `__sec_${s.label}`;
    const sectionNode = {
      id: sectionKey,
      node_type: s.types[0],
      label: `${s.label} (${items.length})`,
      status: null,
      subtitle: null,
    };
    downstream.push({ node: sectionNode, depth: 0, parentKey: null, key: sectionKey, isSection: true, children: items.map(n => n.id) });
    for (const n of items) {
      downstream.push({ node: n, depth: 1, parentKey: sectionKey, key: n.id });
    }
  }

  return { upstream, downstream };
}

function FlatTree({ items }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    const s = {};
    for (const item of items) {
      if (item.children) s[item.key] = true;
    }
    return s;
  });

  const toggle = (key) => setOpen(o => ({ ...o, [key]: !o[key] }));

  const visible = items.filter(item => {
    if (!item.parentKey) return true;
    return open[item.parentKey] !== false;
  });

  return (
    <div className="space-y-0">
      {visible.map(item => (
        <NodeRow
          key={item.key}
          node={item.node}
          depth={item.depth}
          isOpen={!!open[item.key]}
          hasChildren={!!(item.children && item.children.length)}
          onToggle={() => item.children && toggle(item.key)}
          navigate={navigate}
        />
      ))}
    </div>
  );
}

export default function EventGraphExplorer({ graphData, eid }) {
  const { upstream, downstream } = buildFlatList(graphData);
  const meta = graphData.meta;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="event-graph-explorer">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">
          Upstream Lineage
        </div>
        {upstream.length === 0
          ? <p className="text-xs text-slate-400 italic">No upstream records found.</p>
          : <FlatTree items={upstream} />}
      </div>

      <div className="bg-white border border-indigo-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">
          Event Root (Canonical)
        </div>
        <FlatTree items={[{ node: graphData.root, depth: 0, key: graphData.root.id, parentKey: null }]} />
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-base font-bold text-slate-700">{meta.node_count}</div>
            <div className="text-xs text-slate-400">Nodes</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-700">{meta.edge_count}</div>
            <div className="text-xs text-slate-400">Edges</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-700">{meta.warning_count}</div>
            <div className="text-xs text-slate-400">Warnings</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">
          Downstream Entities
        </div>
        {downstream.length === 0
          ? <p className="text-xs text-slate-400 italic">No downstream entities yet.</p>
          : <FlatTree items={downstream} />}
      </div>
    </div>
  );
}
